import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "sbc-pwa-install-dismissed";
const DISMISS_DAYS = 14;

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    return Date.now() - ts < DISMISS_DAYS * 86400 * 1000;
  } catch { return false; }
}

/**
 * PwaInstallPrompt
 * - Listens for the `beforeinstallprompt` event on Chrome/Edge/Android
 * - Shows a floating "Install App" button; tapping opens a bottom sheet
 * - iOS Safari: shows an "Add to Home Screen" instruction sheet (no native prompt)
 * - Fully dismissible for 14 days; re-appears afterwards
 */
export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    const iosFlag = isIOS();
    setIos(iosFlag);

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installed = () => {
      setVisible(false);
      setDeferred(null);
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore quota */ }
    };
    window.addEventListener("appinstalled", installed);

    // iOS never fires beforeinstallprompt — show CTA after a short delay
    if (iosFlag) {
      const t = setTimeout(() => setVisible(true), 3500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handler);
        window.removeEventListener("appinstalled", installed);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore quota */ }
    setVisible(false);
    setShowSheet(false);
  };

  const install = async () => {
    if (!deferred) { setShowSheet(true); return; }
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
      } else {
        // user declined — soft dismiss for a shorter window
        try { localStorage.setItem(DISMISS_KEY, String(Date.now() - (DISMISS_DAYS - 3) * 86400 * 1000)); } catch { /* ignore quota */ }
        setVisible(false);
      }
    } catch {
      setShowSheet(true);
    } finally {
      setDeferred(null);
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Floating install button */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          transition={{ type: "spring", stiffness: 200, damping: 22 }}
          className="fixed z-[70] bottom-[68px] right-4 sm:bottom-6 sm:right-6 flex items-stretch shadow-2xl rounded-full overflow-hidden"
          data-testid="pwa-install-fab"
        >
          <button
            onClick={ios ? () => setShowSheet(true) : install}
            className="flex items-center gap-2 bg-gradient-to-br from-amber-300 via-[#D4AF37] to-amber-600 text-neutral-950 px-4 sm:px-5 py-3 text-sm font-bold uppercase tracking-widest hover:brightness-110 transition"
            data-testid="pwa-install-btn"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Install App</span>
            <span className="sm:hidden">Install</span>
          </button>
          <button
            onClick={dismiss}
            className="bg-neutral-950/90 backdrop-blur-md text-white/70 hover:text-white px-3 border-l border-white/10"
            aria-label="Dismiss install prompt"
            data-testid="pwa-install-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      </AnimatePresence>

      {/* Bottom sheet with install instructions */}
      <AnimatePresence>
        {showSheet && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowSheet(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80]"
            />
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 220 }}
              className="fixed inset-x-0 bottom-0 z-[90] bg-neutral-950 text-white rounded-t-3xl border-t border-white/10 max-h-[85vh] overflow-y-auto"
              data-testid="pwa-install-sheet"
            >
              <div className="w-12 h-1.5 bg-white/20 rounded-full mx-auto mt-3 mb-1" />
              <div className="max-w-md mx-auto px-6 py-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[11px] tracking-widest uppercase text-[#D4AF37] font-semibold">Install</div>
                    <h3 className="text-2xl font-black tracking-tight mt-1">SBC Ltd Brewing</h3>
                    <p className="text-sm text-neutral-400 mt-1">Add the SBC app to your home screen for one-tap access.</p>
                  </div>
                  <button
                    onClick={() => setShowSheet(false)}
                    className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {ios ? (
                  <ol className="mt-6 space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#D4AF37] text-neutral-950 text-xs font-black flex items-center justify-center flex-none">1</span>
                      <span className="text-sm">Tap the <Share className="w-4 h-4 inline align-text-bottom mx-1" /> <b>Share</b> icon at the bottom of Safari.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#D4AF37] text-neutral-950 text-xs font-black flex items-center justify-center flex-none">2</span>
                      <span className="text-sm">Scroll and select <b>Add to Home Screen</b> <Plus className="w-4 h-4 inline align-text-bottom mx-1" /></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#D4AF37] text-neutral-950 text-xs font-black flex items-center justify-center flex-none">3</span>
                      <span className="text-sm">Tap <b>Add</b>. SBC Brewing will now appear as an app on your home screen.</span>
                    </li>
                  </ol>
                ) : (
                  <div className="mt-6 text-sm text-neutral-300">
                    <p>To install SBC Ltd Brewing on this device:</p>
                    <ol className="mt-3 space-y-2 list-decimal list-inside">
                      <li>Open the browser menu (⋮)</li>
                      <li>Choose <b>Install app</b> or <b>Add to Home screen</b></li>
                      <li>Confirm to add — SBC Brewing launches like a native app.</li>
                    </ol>
                  </div>
                )}

                <div className="mt-6 flex gap-2">
                  {!ios && deferred && (
                    <button onClick={install}
                      className="flex-1 bg-[#D4AF37] hover:bg-amber-400 text-neutral-950 rounded-full py-3 text-sm font-bold uppercase tracking-widest">
                      Install now
                    </button>
                  )}
                  <button onClick={dismiss}
                    className="flex-1 bg-white/10 hover:bg-white/15 rounded-full py-3 text-sm font-semibold">
                    Maybe later
                  </button>
                </div>

                <p className="text-[11px] text-neutral-500 text-center mt-5">
                  Free · No app-store account required · Works offline
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
